/**
 * Shared attachment staging UI — drag/drop, clipboard paste, preview, remove, validation feedback.
 * Server EvidencePipeline remains authoritative; this is advisory UX only.
 */

import { preflightAttachments } from './preflight';
import type { SnapEvidenceConstraints } from '../types';
import { safeText } from '../security/sanitize';

export interface AttachmentPickerOptions {
  mount: HTMLElement;
  constraints: SnapEvidenceConstraints;
  onChange: (files: File[]) => void;
  statusTarget?: HTMLElement | null;
  inputId?: string;
}

export class AttachmentPicker {
  private files: File[] = [];
  private readonly list: HTMLElement;
  private readonly input: HTMLInputElement;
  private readonly dropZone: HTMLElement;
  private readonly opts: AttachmentPickerOptions;
  private unbindPaste: (() => void) | null = null;

  constructor(opts: AttachmentPickerOptions) {
    this.opts = opts;

    this.dropZone = document.createElement('div');
    this.dropZone.className = 'nv-snap-dropzone';
    this.dropZone.tabIndex = 0;
    this.dropZone.setAttribute('role', 'group');
    this.dropZone.setAttribute('aria-label', 'Attachment drop zone');

    const hint = document.createElement('p');
    hint.className = 'nv-snap-muted';
    safeText(
      hint,
      'Drop files here, paste from clipboard, or browse. Images show a preview before submit.'
    );

    this.input = document.createElement('input');
    this.input.type = 'file';
    this.input.multiple = true;
    this.input.id = opts.inputId || 'nv-snap-files';
    this.input.setAttribute('aria-label', 'Attachments');
    this.input.addEventListener('change', () => {
      this.addFiles(Array.from(this.input.files || []));
      this.input.value = '';
    });

    this.list = document.createElement('ul');
    this.list.className = 'nv-snap-attachment-list';
    this.list.setAttribute('aria-live', 'polite');

    this.dropZone.append(hint, this.input, this.list);
    opts.mount.appendChild(this.dropZone);

    this.dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropZone.dataset.drag = '1';
    });
    this.dropZone.addEventListener('dragleave', () => {
      delete this.dropZone.dataset.drag;
    });
    this.dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      delete this.dropZone.dataset.drag;
      const dropped = Array.from(e.dataTransfer?.files || []);
      this.addFiles(dropped);
    });

    const onPaste = (e: ClipboardEvent) => {
      if (!this.isRelevantFocus()) return;
      const items = Array.from(e.clipboardData?.items || []);
      const pasted: File[] = [];
      for (const item of items) {
        if (item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (file) pasted.push(file);
      }
      if (pasted.length) {
        e.preventDefault();
        this.addFiles(pasted);
      }
    };
    document.addEventListener('paste', onPaste);
    this.unbindPaste = () => document.removeEventListener('paste', onPaste);
  }

  getFiles(): File[] {
    return this.files.slice();
  }

  setDisabled(disabled: boolean): void {
    this.input.disabled = disabled;
    this.dropZone.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }

  clear(): void {
    this.files = [];
    this.render();
    this.opts.onChange([]);
  }

  destroy(): void {
    this.unbindPaste?.();
    this.dropZone.remove();
  }

  private isRelevantFocus(): boolean {
    const active = document.activeElement;
    if (!active) return false;
    return this.dropZone.contains(active) || active === document.body || active === document.documentElement;
  }

  private addFiles(incoming: File[]): void {
    if (!incoming.length) return;
    const next = [...this.files, ...incoming];
    const result = preflightAttachments(next, this.opts.constraints);
    if (!result.ok) {
      this.setStatus(result.error.message || 'Attachment rejected.', 'error');
      return;
    }
    this.files = result.files;
    this.render();
    this.opts.onChange(this.files.slice());
    this.setStatus(
      this.files.length
        ? `${this.files.length} attachment${this.files.length === 1 ? '' : 's'} ready.`
        : '',
      'info'
    );
  }

  private removeAt(index: number): void {
    this.files = this.files.filter((_, i) => i !== index);
    this.render();
    this.opts.onChange(this.files.slice());
  }

  private render(): void {
    this.list.innerHTML = '';
    this.files.forEach((file, index) => {
      const li = document.createElement('li');
      li.className = 'nv-snap-attachment-item';
      const meta = document.createElement('div');
      meta.className = 'nv-snap-attachment-meta';
      safeText(meta, `${file.name} (${formatBytes(file.size)})`);

      if (file.type.startsWith('image/')) {
        const img = document.createElement('img');
        img.className = 'nv-snap-attachment-thumb';
        img.alt = file.name;
        img.src = URL.createObjectURL(file);
        img.onload = () => URL.revokeObjectURL(img.src);
        li.appendChild(img);
      }

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'nv-snap-btn';
      safeText(remove, 'Remove');
      remove.addEventListener('click', () => this.removeAt(index));
      li.append(meta, remove);
      this.list.appendChild(li);
    });
  }

  private setStatus(message: string, tone: 'info' | 'error'): void {
    if (!this.opts.statusTarget) return;
    safeText(this.opts.statusTarget, message);
    this.opts.statusTarget.setAttribute('data-tone', tone);
  }
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
